import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Car, Menu, X, Globe, Phone, LayoutDashboard, LogOut, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { useT } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../lib/useSettings";

export const Navbar = () => {
  const { t, lang, setLang } = useT();
  const { user, logout } = useAuth();
  const settings = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/fleet", label: t("nav.fleet") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const brand = settings.company_name || "ClickRent";
  const wa = settings.whatsapp ? `https://wa.me/${settings.whatsapp}` : "#";

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/90 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="bg-black text-white p-1.5 rounded-md"><Car className="h-5 w-5" /></div>
          <span className="font-heading font-extrabold text-lg tracking-tight">{brand.split(" ")[0]}<span className="text-accent">{brand.split(" ").slice(1).join(" ") || "Rent"}</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{l.label}</Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={() => setLang(lang === "ro" ? "en" : "ro")} data-testid="lang-toggle"
            className="flex items-center gap-1 text-sm font-semibold px-2 py-1 hover:bg-secondary rounded-md transition-colors">
            <Globe className="h-4 w-4" /> {lang.toUpperCase()}
          </button>

          {user && (
            <>
              <Button variant="outline" size="sm" className="rounded-md gap-2 hidden sm:flex" onClick={() => navigate("/admin")} data-testid="nav-admin-btn">
                <LayoutDashboard className="h-4 w-4" /> {t("nav.admin")}
              </Button>
              <Button variant="ghost" size="sm" className="rounded-md" onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          )}

          <a href={wa} target="_blank" rel="noreferrer" data-testid="nav-whatsapp-btn" className="hidden sm:inline-flex">
            <Button size="sm" className="rounded-md bg-accent hover:bg-accent/90 gap-2">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </Button>
          </a>

          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background px-6 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm font-medium py-1" data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}>{l.label}</Link>
          ))}
          <a href={wa} target="_blank" rel="noreferrer"><Button className="w-full rounded-md bg-accent hover:bg-accent/90 gap-2"><MessageCircle className="h-4 w-4" /> WhatsApp</Button></a>
          {settings.phone && <a href={`tel:${settings.phone}`} className="flex items-center gap-2 text-sm py-1"><Phone className="h-4 w-4" /> {settings.phone}</a>}
        </div>
      )}
    </header>
  );
};
